import { memo, useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { notifications } from '@mantine/notifications';
import {
    IconArrowRight,
    IconBrandGithubFilled,
    IconBrandGoogleFilled,
    IconExclamationCircle,
} from '@tabler/icons-react';
import { Endpoints } from '@xver/apis';
import { InputField, Tooltip, TurnstileWithSkeleton } from '@xver/components/Atoms';
import Button from '@xver/components/Atoms/Button/Button';

import { PAGE_TYPE, type PageType } from '../../constants';
import { useSignIn } from './hooks/useSignIn';

const MAX_WIDTH_STYLE = { maxWidth: '400px' } as const;
const DIVIDER_CLASS = 'grow border-t-[0.5px] border-fg/38';
const FORGET_PASSWORD_CLASS =
    'inline-flex min-h-6 items-center text-xs text-fg/68 hover:underline transition-allease-in-out duration-150';
const SIGN_UP_LINK_CLASS =
    'ml-1 text-sm text-blue-400 light:text-blue-700 hover:underline transition-allease-in-out duration-150';

const ProviderButtons = memo(() => {
    const { t } = useTranslation('auth');
    const [githubLoading, setGithubLoading] = useState(false);

    const handleGithub = useCallback(async () => {
        setGithubLoading(true);
        try {
            const res = await Endpoints.v1.Authentication.getGithubAuthorizeUrl();
            const authorizeUrl = res?.data?.authorizeUrl;
            if (!authorizeUrl) {
                throw new Error('missing authorize url');
            }

            sessionStorage.setItem('githubOAuthMode', 'login');
            window.location.href = authorizeUrl;
        } catch {
            setGithubLoading(false);
            notifications.show({
                title: t('signIn.githubFailedTitle', 'GitHub sign-in failed'),
                message: t(
                    'signIn.githubFailedMessage',
                    'Unable to start GitHub sign-in. Please try again.'
                ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        }
    }, [t]);

    return (
        <div className="w-full flex flex-col justify-between items-center gap-2">
            <Button
                width="100%"
                className="p-2"
                variant={'secondary'}
                leftIcon={<IconBrandGithubFilled size={20} />}
                isLoading={githubLoading}
                onClick={handleGithub}
            >
                {t('signIn.continueWithGithub')}
            </Button>
            <Tooltip className="w-full" content={t('signIn.comingSoon')} position="top">
                <Button
                    disabled
                    width="100%"
                    className="p-2 text-fg"
                    variant={'secondary'}
                    leftIcon={<IconBrandGoogleFilled size={20} />}
                >
                    {t('signIn.continueWithGoogle')}
                </Button>
            </Tooltip>
        </div>
    );
});

const Divider = memo(() => {
    const { t } = useTranslation('auth');
    return (
        <div className="relative w-full flex items-center">
            <div className={DIVIDER_CLASS}></div>
            <span className="shrink text-[10px] text-fg/68" style={{ margin: '0 0.6rem' }}>
                {t('signIn.orUppercase')}
            </span>
            <div className={DIVIDER_CLASS}></div>
        </div>
    );
});

interface SignInFormProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

function SignInForm({ setPageType }: SignInFormProps) {
    const { t } = useTranslation(['auth', 'validation']);
    const { isSubmitting, register, setValue, onSubmit: handleSignIn } = useSignIn({ setPageType });

    const handleResetPassword = useCallback(() => {
        setPageType(PAGE_TYPE.RESET_PASSWORD);
    }, [setPageType]);

    const handleSignUp = useCallback(() => {
        setPageType(PAGE_TYPE.SIGN_UP);
    }, [setPageType]);

    return (
        <form
            autoComplete="off"
            onSubmit={handleSignIn}
            className="w-full flex flex-col justify-start items-center"
        >
            <div
                className="w-full flex flex-col justify-center items-center gap-4"
                style={MAX_WIDTH_STYLE}
            >
                <ProviderButtons />
                <Divider />
                <div className="w-full flex flex-col justify-center items-center gap-3">
                    <InputField
                        label={t('signIn.emailLabel')}
                        size="sm"
                        type="email"
                        {...register('email', { required: t('validation:email.required') })}
                        placeholder={t('signIn.emailLabel')}
                        required
                    />
                    <InputField
                        label={t('signIn.passwordLabel')}
                        labelRight={
                            <Link
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleResetPassword();
                                }}
                                className={FORGET_PASSWORD_CLASS}
                            >
                                {t('signIn.forgetPassword')}
                            </Link>
                        }
                        size="sm"
                        type="password"
                        {...register('password', { required: t('validation:password.required') })}
                        placeholder={t('signIn.passwordLabel')}
                        passwordReveal
                        required
                    />
                </div>

                <div className="w-full flex flex-col justify-start items-start gap-1">
                    <span className="text-xs text-fg/65">{t('signIn.letUsKnowYouAreHuman')}</span>
                    <TurnstileWithSkeleton
                        theme="dark"
                        size="flexible"
                        className="captcha"
                        onVerify={(token) => setValue('turnstileToken', token)}
                        onExpire={() => setValue('turnstileToken', '')}
                        onError={() => setValue('turnstileToken', '')}
                    />
                </div>

                <Button
                    width="100%"
                    type="submit"
                    isLoading={isSubmitting}
                    rightIcon={<IconArrowRight size={16} stroke={1.6} />}
                >
                    {t('signIn.submit')}
                </Button>

                <div className="flex items-center gap-1">
                    <span className="text-sm text-fg/78">{t('signIn.noAccount')}</span>
                    <Link
                        to="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSignUp();
                        }}
                        className={SIGN_UP_LINK_CLASS}
                    >
                        {t('signIn.signUpLink')}
                    </Link>
                </div>
            </div>
        </form>
    );
}

export default memo(SignInForm);
